import classNames from 'classnames'
import {useFeatureFlagEnabled} from 'posthog-js/react'
import Field from '../Field.js'
import {FieldChoiceGroup} from '../FieldGroup.js'
import {CLASS_SCHEDULE_TIMES} from '@/types/ClassScheduleTimes'
import {
  CLASS_TIME_SLOTS,
  DEGREE_AWARE_CLASS_TIMES_FLAG,
  getClassTimeLabel,
} from './classTimes'
import RegularMoonIcon from '@/components/icons/regular/moon'
import RegularSunriseIcon from '@/components/icons/regular/sunrise'
import RegularSunsetIcon from '@/components/icons/regular/sunset'
import styles from './ApplicationForm.module.scss'

// The slot icon keys (types/ClassScheduleTimes.ts) as page icons.
const SLOT_ICONS = {
  sunrise: RegularSunriseIcon,
  sunset: RegularSunsetIcon,
  moon: RegularMoonIcon,
}

function SlotIcon({slot}) {
  const Icon = SLOT_ICONS[CLASS_SCHEDULE_TIMES[slot].icon]
  return (
    <span className={styles['slot-icon']}>
      <Icon />
    </span>
  )
}

/** Whether the class-time questions follow the degree's offered slots. */
export function useDegreeAwareClassTimes() {
  return useFeatureFlagEnabled(DEGREE_AWARE_CLASS_TIMES_FLAG) === true
}

/**
 * A class-time group's question over its hint. On the standalone class-times
 * page (`page`) the question is the page's subhead, an h4 and its hint
 * (`slots__*`). Inside an application step (`form`) it is the legend every
 * other question group on the form has: the label type, with the hint as the
 * second, muted line (the same two lines as Goals.js's checkbox group).
 */
function SlotsLegend({layout, question, hint, later = false}) {
  if (layout === 'form') {
    return (
      <>
        <span className={styles['legend-line']}>{question}</span>
        <span
          className={classNames(
            styles['legend-line'],
            styles['legend-line--hint'],
          )}
        >
          {hint}
        </span>
      </>
    )
  }
  return (
    <>
      <h4
        className={classNames(styles['slots__question'], {
          [styles['slots__question--later']]: later,
        })}
      >
        {question}
      </h4>
      <span className={styles['slots__hint']}>{hint}</span>
    </>
  )
}

const SELECT_ALL = 'Select all that apply.'

/**
 * The class-time checkboxes: with degree-aware class times, the degree's
 * offered slots under "When do you prefer to take classes for {degree}?" and
 * the rest under "Future class time interest"; without, all three slots under
 * one question. Shared by the post-submit class-times page (`layout="page"`,
 * the default) and the "Program of Interest" step (`layout="form"`, which
 * draws the questions as the form's other field groups and namespaces its
 * fields with `namePrefix`).
 *
 * `name` names the preferred group's Formik field for its error rule; `error`
 * is the text the group shows under the caller's own condition.
 */
function ClassTimeSlots({
  site,
  degreeName,
  degreeAware,
  preferredSlots,
  optionalSlots = [],
  namePrefix = '',
  name,
  error,
  layout = 'page',
}) {
  const sacramento = site === 'sacramento'
  const slotField = slot => (
    <Field
      key={slot}
      type="checkbox"
      name={`${namePrefix}${slot}`}
      label={getClassTimeLabel(slot, {sacramento})}
      icon={<SlotIcon slot={slot} />}
    />
  )

  if (!degreeAware) {
    return (
      <FieldChoiceGroup
        name={name}
        legend={
          <SlotsLegend
            layout={layout}
            question="When do you prefer to take classes?"
            hint={SELECT_ALL}
          />
        }
        error={error}
      >
        {CLASS_TIME_SLOTS.map(slotField)}
      </FieldChoiceGroup>
    )
  }

  return (
    <>
      <FieldChoiceGroup
        name={name}
        legend={
          <SlotsLegend
            layout={layout}
            question={`When do you prefer to take classes for ${
              degreeName ?? 'your program'
            }?`}
            hint={SELECT_ALL}
          />
        }
        error={error}
      >
        {preferredSlots.map(slotField)}
      </FieldChoiceGroup>

      {optionalSlots.length > 0 && (
        <FieldChoiceGroup
          legend={
            <SlotsLegend
              layout={layout}
              question="Future class time interest"
              hint="Please indicate if you'd be interested in future availability for:"
              later
            />
          }
        >
          {optionalSlots.map(slotField)}
        </FieldChoiceGroup>
      )}
    </>
  )
}

export default ClassTimeSlots
